import { tokenize } from './tokenizer';

export function normalizeSearchValue(str) {
    return String(str ?? '')
        .replace(/&amp;/gi, '&')
        .replace(/&#0*38;/g, '&')
        .replace(/&nbsp;/gi, ' ')
        .replace(/[''`´]/g, "'")
        .toLowerCase()
        .trim();
}

export function compactSearchText(str) {
    return normalizeSearchValue(str).replace(/[^a-z0-9]/g, '');
}

function flexSlashText(str) {
    return normalizeSearchValue(str).replace(/\s*\/\s*/g, '/');
}

function tokenCompact(token) {
    return compactSearchText(token);
}

function minCompactLen(token) {
    if (typeof token !== 'string' || token.length === 0) return 2;
    if (isCodeOnlySearchToken(token)) return 2;
    if (token.length >= 2) return 1;
    return 2;
}

function apostropheFlexibleMatch(text, token) {
    if (!text || !token) return false;
    const letters = token.replace(/[^a-z0-9]/g, '');
    if (letters.length === 0) return false;
    const normalized = text.replace(/[''`´]/g, "'");
    if (normalized.includes(token)) return true;
    if (letters.length === 1) {
        return (
            normalized.includes(`'${letters}`) ||
            normalized.includes(`${letters}'`) ||
            normalized.includes(` ${letters} `) ||
            normalized.endsWith(` ${letters}`) ||
            normalized.endsWith(`'${letters}'`)
        );
    }
    const pattern = letters.split('').join("[''`´]?");
    try {
        return new RegExp(pattern, 'i').test(normalized);
    } catch {
        return false;
    }
}

export function isSpecialSearchToken(token) {
    return typeof token === 'string' && /[^a-z0-9]/i.test(token);
}

export function isCodeOnlySearchToken(token) {
    if (typeof token !== 'string' || token.length === 0) return false;
    return /^\d+$/.test(token);
}

export function isPackSizeLikeToken(token) {
    if (typeof token !== 'string' || token.length === 0) return false;
    const t = token.replace(/^[(\[]+/, '');
    if (/^\d+(g|kg|ml|l|oz|mg|lb)s?$/i.test(t)) return true;
    if (/^\d+x\d+(g|kg|ml|l|oz|mg|lb)?s?$/i.test(t)) return true;
    if (/^\d+x\d+$/i.test(t)) return true;
    return false;
}

function getFields(row) {
    const codeL = normalizeSearchValue(row?.product_code ?? row?.code ?? '');
    const nameL = normalizeSearchValue(row?.product_name ?? row?.name ?? '');
    return {
        codeL,
        nameL,
        codeFlex: flexSlashText(codeL),
        nameFlex: flexSlashText(nameL),
        codeC: compactSearchText(codeL),
        nameC: compactSearchText(nameL),
    };
}

function hasLeadingSpecial(token) {
    return typeof token === 'string' && /^[^a-z0-9]+/i.test(token);
}

function isPartialPackParenToken(token) {
    return typeof token === 'string' && /^[(\[]+\d/i.test(token);
}

function partialPackParenTokenMatches(f, token) {
    return (
        literalSpecialIncludes(f.codeL, token) ||
        literalSpecialIncludes(f.nameL, token) ||
        literalSpecialIncludes(f.codeFlex, token) ||
        literalSpecialIncludes(f.nameFlex, token)
    );
}

function shouldUseCompactFallback(token) {
    if (isPartialPackParenToken(token) || hasLeadingSpecial(token)) return false;
    return true;
}

function literalSpecialIncludes(text, token) {
    if (!text || !token) return false;
    return text.includes(token);
}

function getNameWordsForSequence(f) {
    return (f.nameFlex || f.nameL).split(/\s+/).filter(Boolean);
}

function wordMatchesSearchToken(word, token) {
    if (!word || !token) return false;
    if (word.startsWith(token)) return true;
    const tc = tokenCompact(token);
    const minLen = minCompactLen(token);
    if (tc.length >= minLen && compactSearchText(word).startsWith(tc)) return true;
    if (word.includes('/')) {
        for (const seg of word.split('/').filter(Boolean)) {
            if (seg.startsWith(token)) return true;
            if (tc.length >= minLen && compactSearchText(seg).startsWith(tc)) return true;
        }
    }
    return false;
}

function findWordIndexForToken(words, token, fromIndex) {
    for (let i = fromIndex; i < words.length; i++) {
        if (wordMatchesSearchToken(words[i], token)) return i;
    }
    return -1;
}

function sequentialNameMatchBonus(f, tokens) {
    if (!Array.isArray(tokens) || tokens.length < 2) return 0;
    const words = getNameWordsForSequence(f);
    if (words.length === 0) return 0;

    let wordIndex = findWordIndexForToken(words, tokens[0], 0);
    if (wordIndex < 0) return 0;

    let bonus = wordIndex === 0 ? 30 : Math.max(0, 20 - wordIndex * 5);
    let prev = wordIndex;

    for (let i = 1; i < tokens.length; i++) {
        const next = findWordIndexForToken(words, tokens[i], prev + 1);
        if (next < 0) return 0;
        const gap = next - prev - 1;
        bonus += Math.max(5, 150 - gap * 60);
        prev = next;
    }

    return bonus;
}

function numericInCode(f, token) {
    if (!/^\d+$/.test(token)) return false;
    if (f.codeL === token || f.codeC === token) return true;
    if (f.codeL.startsWith(token) || f.codeC.startsWith(token)) return true;
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(?:^|[^a-z0-9])${escaped}(?:[^a-z0-9]|$)`, 'i');
    return re.test(f.codeL) || re.test(f.codeC);
}

function numericWeightInName(f, token) {
    if (!/^\d+$/.test(token)) return false;
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(?:\\d+x)?${escaped}(?:g|kg)\\b`, 'i');
    return re.test(f.nameL) || re.test(f.nameFlex);
}

function numericTokenMatches(f, token) {
    return numericInCode(f, token) || numericWeightInName(f, token);
}

function codeStartsWith(f, token) {
    if (token && (f.codeL.startsWith(token) || f.codeFlex.startsWith(token))) return true;
    if (!shouldUseCompactFallback(token)) return false;
    const tc = tokenCompact(token);
    return tc.length >= minCompactLen(token) && f.codeC.startsWith(tc);
}

function codeIncludes(f, token) {
    if (token && (f.codeL.includes(token) || f.codeFlex.includes(token))) return true;
    if (!shouldUseCompactFallback(token)) return false;
    const tc = tokenCompact(token);
    return tc.length >= minCompactLen(token) && f.codeC.includes(tc);
}

function nameStartsWith(f, token) {
    if (token && (f.nameL.startsWith(token) || f.nameFlex.startsWith(token))) return true;
    if (!shouldUseCompactFallback(token)) return false;
    const tc = tokenCompact(token);
    return tc.length >= minCompactLen(token) && f.nameC.startsWith(tc);
}

function nameWordStartsWith(f, token) {
    if (isPartialPackParenToken(token) || hasLeadingSpecial(token)) {
        return partialPackParenTokenMatches(f, token);
    }
    const tc = tokenCompact(token);
    const minLen = minCompactLen(token);
    const wordLists = [
        f.nameL.split(/[\s([{]+/).filter(Boolean),
        f.nameFlex.split(/[\s([{]+/).filter(Boolean),
        f.nameL.split(/\s+/).filter(Boolean),
        f.nameFlex.split(/\s+/).filter(Boolean),
    ];
    for (const words of wordLists) {
        if (words.some((w) => {
            if (w.startsWith(token)) return true;
            return tc.length >= minLen && compactSearchText(w).startsWith(tc);
        })) {
            return true;
        }
    }
    const compactWords = f.nameC.match(/[a-z0-9]+/g) || [];
    return tc.length >= minLen && compactWords.some((w) => w.startsWith(tc));
}

function nameIncludes(f, token) {
    if (token && (f.nameL.includes(token) || f.nameFlex.includes(token))) return true;
    if (apostropheFlexibleMatch(f.nameL, token)) return true;
    if (!shouldUseCompactFallback(token)) return false;
    const tc = tokenCompact(token);
    return tc.length >= minCompactLen(token) && f.nameC.includes(tc);
}

function specialTokenMatches(f, token, isFirst) {
    if (isPartialPackParenToken(token) || hasLeadingSpecial(token)) {
        return partialPackParenTokenMatches(f, token);
    }
    if (f.codeL.startsWith(token) || f.nameL.startsWith(token)) return true;
    if (f.codeFlex.startsWith(token) || f.nameFlex.startsWith(token)) return true;
    if (apostropheFlexibleMatch(f.nameL, token) || apostropheFlexibleMatch(f.codeL, token)) return true;
    if (codeStartsWith(f, token)) return true;
    if (isFirst) {
        if (nameStartsWith(f, token)) return true;
        return nameWordStartsWith(f, token);
    }
    return nameWordStartsWith(f, token);
}

function firstTokenMatches(f, token) {
    if (hasLeadingSpecial(token)) return partialPackParenTokenMatches(f, token);
    if (isCodeOnlySearchToken(token)) return numericTokenMatches(f, token);
    if (isPackSizeLikeToken(token)) return codeIncludes(f, token) || nameIncludes(f, token);
    if (isSpecialSearchToken(token)) return specialTokenMatches(f, token, true);
    if (codeStartsWith(f, token)) return true;
    if (nameStartsWith(f, token)) return true;
    return nameWordStartsWith(f, token);
}

function laterTokenMatches(f, token) {
    if (hasLeadingSpecial(token)) return partialPackParenTokenMatches(f, token);
    if (isCodeOnlySearchToken(token)) return numericTokenMatches(f, token);
    if (isPackSizeLikeToken(token)) {
        return codeIncludes(f, token) || nameIncludes(f, token) || nameWordStartsWith(f, token);
    }
    if (isSpecialSearchToken(token)) return specialTokenMatches(f, token, false);
    if (token.length === 1 && /[a-z]/i.test(token)) {
        return nameWordStartsWith(f, token) || codeStartsWith(f, token);
    }
    if (codeStartsWith(f, token)) return true;
    if (nameStartsWith(f, token)) return true;
    return nameWordStartsWith(f, token);
}

function scoreToken(f, token, isFirst) {
    let best = 0;

    if (isCodeOnlySearchToken(token)) {
        if (numericInCode(f, token) && (f.codeL.startsWith(token) || f.codeC.startsWith(token))) {
            best = Math.max(best, 100);
        } else if (numericInCode(f, token)) {
            best = Math.max(best, 60);
        } else if (numericWeightInName(f, token)) {
            best = Math.max(best, 40);
        }
        return best;
    }

    if (hasLeadingSpecial(token)) {
        if (partialPackParenTokenMatches(f, token)) best = Math.max(best, 80);
        return best;
    }

    if (isPackSizeLikeToken(token)) {
        if (codeStartsWith(f, token)) best = Math.max(best, 100);
        else if (codeIncludes(f, token)) best = Math.max(best, 60);
        if (nameIncludes(f, token)) best = Math.max(best, 20);
        return best;
    }

    if (codeStartsWith(f, token)) best = Math.max(best, 100);
    else if (codeIncludes(f, token)) best = Math.max(best, 60);

    if (isFirst) {
        if (nameStartsWith(f, token)) best = Math.max(best, 40);
        else if (nameWordStartsWith(f, token)) best = Math.max(best, 40);
    } else {
        if (nameIncludes(f, token)) best = Math.max(best, 20);
        else if (nameWordStartsWith(f, token)) best = Math.max(best, 20);
        if (codeIncludes(f, token)) best = Math.max(best, 6);
    }

    return best;
}

export function tokenMatchesAtIndex(i, codeL, nameL, token) {
    const f = {
        codeL: normalizeSearchValue(codeL),
        nameL: normalizeSearchValue(nameL),
        codeFlex: flexSlashText(codeL),
        nameFlex: flexSlashText(nameL),
        codeC: compactSearchText(codeL),
        nameC: compactSearchText(nameL),
    };
    if (i === 0) return firstTokenMatches(f, token);
    return laterTokenMatches(f, token);
}

export function buildProductSearchTokens(row) {
    const code = normalizeSearchValue(row?.product_code ?? row?.code ?? '');
    const name = normalizeSearchValue(row?.product_name ?? row?.name ?? '');
    const tokens = new Set();

    [code, name, flexSlashText(code), flexSlashText(name), compactSearchText(code), compactSearchText(name)]
        .filter(Boolean)
        .forEach((value) => tokens.add(value));

    name.split(/\s+/).filter(Boolean).forEach((word) => tokens.add(word));

    for (const compact of [compactSearchText(code), compactSearchText(name)]) {
        if (!compact) continue;
        tokens.add(compact);
        (compact.match(/[a-z]+|\d+/g) || []).forEach((piece) => {
            if (piece) tokens.add(piece);
        });
    }

    return Array.from(tokens);
}

function getProductSearchTokens(row) {
    if (Array.isArray(row?.searchTokens) && row.searchTokens.length > 0) {
        return row.searchTokens;
    }
    return buildProductSearchTokens(row);
}

function productMatchesSearchTokensIndex(searchTokens, tokens) {
    if (!Array.isArray(searchTokens) || searchTokens.length === 0) return false;
    return tokens.every((token) =>
        searchTokens.some((entry) => entry === token || entry.startsWith(token))
    );
}

function productMatchesAdvanced(row, tokens) {
    const f = getFields(row);
    return tokens.every((t, i) => (i === 0 ? firstTokenMatches(f, t) : laterTokenMatches(f, t)));
}

function indexRelevanceScore(row, tokens) {
    let score = 0;
    const code = normalizeSearchValue(row?.product_code ?? row?.code ?? '');
    const searchTokens = getProductSearchTokens(row);

    tokens.forEach((token) => {
        if (code === token) score += 10000;
        else if (code.startsWith(token)) score += 5000;
        else if (code.includes(token)) score += 2500;

        searchTokens.forEach((entry) => {
            if (entry === token) score += 500;
            else if (entry.startsWith(token)) score += 200;
            else if (entry.includes(token)) score += 50;
        });
    });

    return score;
}

export function productMatchesSearchTokens(row, tokens) {
    if (!Array.isArray(tokens) || tokens.length === 0) return false;
    if (productMatchesAdvanced(row, tokens)) return true;
    return productMatchesSearchTokensIndex(getProductSearchTokens(row), tokens);
}

export function productSearchRelevanceScore(row, tokens) {
    if (!Array.isArray(tokens) || tokens.length === 0) return 0;
    const f = getFields(row);
    let score = 0;
    let matchCount = 0;
    let firstOnCode = false;
    let firstOnCodePrefix = false;
    let restAllOnName = true;

    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        const matched = i === 0 ? firstTokenMatches(f, token) : laterTokenMatches(f, token);
        if (!matched) continue;

        matchCount++;
        score += scoreToken(f, token, i === 0);

        if (i === 0) {
            firstOnCodePrefix = codeStartsWith(f, token);
            firstOnCode = codeIncludes(f, token);
        } else if (!nameIncludes(f, token) && !nameWordStartsWith(f, token)) {
            restAllOnName = false;
        }
    }

    if (matchCount === tokens.length && matchCount > 0) {
        score += 200;

        if (tokens.length >= 2) {
            score += sequentialNameMatchBonus(f, tokens);
        }

        if (tokens.length >= 2 && firstOnCode && restAllOnName) {
            score += firstOnCodePrefix ? 120 : 80;
        }

        score += Math.min(Math.floor(indexRelevanceScore(row, tokens) / 200), 50);
        return score;
    }

    if (matchCount > 0) {
        return score;
    }

    if (productMatchesSearchTokensIndex(getProductSearchTokens(row), tokens)) {
        return Math.floor(indexRelevanceScore(row, tokens) / 50);
    }

    return 0;
}

function compareProductCodesNaturally(a, b) {
    const codeA = String(a?.product_code ?? a?.code ?? '').toUpperCase().trim();
    const codeB = String(b?.product_code ?? b?.code ?? '').toUpperCase().trim();

    const rawCompare = codeA.localeCompare(codeB, undefined, {
        sensitivity: 'base',
    });
    if (rawCompare !== 0) return rawCompare;

    const nameA = String(a?.product_name ?? a?.name ?? '');
    const nameB = String(b?.product_name ?? b?.name ?? '');
    return nameA.localeCompare(nameB, undefined, {
        sensitivity: 'base',
    });
}

function bothProductsMatchCodePrefix(productA, productB, token) {
    if (!token) return false;
    const fieldsA = getFields(productA);
    const fieldsB = getFields(productB);
    return codeStartsWith(fieldsA, token) && codeStartsWith(fieldsB, token);
}

export function sortProductsBySearchRelevance(products, tokens) {
    if (!Array.isArray(products)) return [];
    if (!Array.isArray(tokens) || tokens.length === 0) return products.slice();
    const firstToken = tokens[0];
    return products.slice().sort((a, b) => {
        if (firstToken && bothProductsMatchCodePrefix(a, b, firstToken)) {
            const codeCompare = compareProductCodesNaturally(a, b);
            if (codeCompare !== 0) return codeCompare;
        }

        const scoreDiff = productSearchRelevanceScore(b, tokens) - productSearchRelevanceScore(a, tokens);
        if (scoreDiff !== 0) return scoreDiff;

        return compareProductCodesNaturally(a, b);
    });
}

export function searchTokensFromQuery(query) {
    return tokenize(normalizeSearchValue(String(query ?? '').trim()));
}

export function hasSearchableProductQuery(query) {
    return searchTokensFromQuery(query).length > 0;
}

/**
 * Blanks out the field(s) not relevant to the given search mode so the existing
 * code/name matching pipeline naturally restricts itself to the chosen field.
 * mode: 'code' | 'name' | 'all' (default, returns row unchanged).
 */
export function maskProductForSearchMode(row, mode) {
    if (mode === 'code') {
        return { ...row, product_name: '', name: '', searchTokens: undefined };
    }
    if (mode === 'name') {
        return { ...row, product_code: '', code: '', searchTokens: undefined };
    }
    return row;
}

/**
 * Filters and sorts products for the given query, restricted to 'code', 'name',
 * or 'all' (code first, then name — the existing combined behavior).
 */
export function filterAndSortProductsForSearch(products, query, mode = 'all', limit = 80) {
    const tokens = searchTokensFromQuery(query);
    if (tokens.length === 0) return [];
    const list = Array.isArray(products) ? products : [];
    const effectiveMode = mode === 'code' || mode === 'name' ? mode : 'all';
    const maxResults = Number.isFinite(limit) && limit > 0 ? limit : 0;

    if (effectiveMode === 'all') {
        const matched = list.filter((item) => productMatchesSearchTokens(item, tokens));
        const sorted = sortProductsBySearchRelevance(matched, tokens);
        return maxResults ? sorted.slice(0, maxResults) : sorted;
    }

    const maskedMatched = [];
    const originalByMasked = new Map();
    for (let i = 0; i < list.length; i++) {
        const original = list[i];
        const masked = maskProductForSearchMode(original, effectiveMode);
        if (!productMatchesSearchTokens(masked, tokens)) continue;
        maskedMatched.push(masked);
        originalByMasked.set(masked, original);
    }
    const sorted = sortProductsBySearchRelevance(maskedMatched, tokens).map((m) => originalByMasked.get(m));
    return maxResults ? sorted.slice(0, maxResults) : sorted;
}
